import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import API_CONFIG from '../../config/api';

/**
 * Formulário público (sem login) para enviar proposta + documentos
 * conforme requisitos de habilitação/qualificação do TR.
 */
export default function PropostaPublicaEditalDialog({ open, onClose, edital, onEnviado }) {
  const codigo = edital?.codigo || '';
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [requisitos, setRequisitos] = useState(null);
  const [form, setForm] = useState({
    razaoSocial: '',
    cnpj: '',
    email: '',
    valor: '',
    descricao: '',
  });
  const [filesByKey, setFilesByKey] = useState({});

  useEffect(() => {
    if (!open || !codigo) return;
    setErro('');
    setOkMsg('');
    setFilesByKey({});
    setLoading(true);
    fetch(`${API_CONFIG.BACKEND_URL}/api/public/editais/${encodeURIComponent(codigo)}/requisitos`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Erro ao carregar requisitos');
        setRequisitos(data);
      })
      .catch((e) => setErro(e.message || 'Erro ao carregar requisitos'))
      .finally(() => setLoading(false));
  }, [open, codigo]);

  const docs = useMemo(
    () => (Array.isArray(requisitos?.documentos) ? requisitos.documentos : []),
    [requisitos]
  );

  const baixarTr = () => {
    window.open(
      `${API_CONFIG.BACKEND_URL}/api/public/editais/${encodeURIComponent(codigo)}/tr`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const enviar = async () => {
    setErro('');
    setOkMsg('');
    if (!form.razaoSocial.trim() || !form.cnpj.trim() || !form.email.trim()) {
      setErro('Preencha razão social, CNPJ e e-mail.');
      return;
    }
    const anexos = docs
      .map((d) => ({ tipo: d.label || d.key, file: filesByKey[d.key] }))
      .filter((x) => x.file);
    if (!anexos.length) {
      setErro('Anexe ao menos a proposta e os documentos solicitados (PDF).');
      return;
    }

    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('razaoSocial', form.razaoSocial.trim());
      fd.append('cnpj', form.cnpj.trim());
      fd.append('email', form.email.trim());
      if (form.valor) fd.append('valor', form.valor);
      if (form.descricao) fd.append('descricao', form.descricao.trim());
      fd.append('tiposDocumento', JSON.stringify(anexos.map((a) => a.tipo)));
      anexos.forEach((a) => fd.append('arquivos', a.file));

      const res = await fetch(
        `${API_CONFIG.BACKEND_URL}/api/public/editais/${encodeURIComponent(codigo)}/proposta`,
        { method: 'POST', body: fd }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao enviar proposta');

      setOkMsg(data.message || 'Proposta enviada com sucesso.');
      onEnviado?.(data);
    } catch (e) {
      setErro(e.message || 'Erro ao enviar');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        Enviar proposta — {codigo}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Sem necessidade de login. Baixe o TR, leia a cláusula de habilitação/qualificação técnica
              e envie a proposta comercial + documentos em PDF.
            </Typography>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={baixarTr}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
            >
              Baixar Termo de Referência (PDF)
            </Button>

            {requisitos?.clausulaTitulo && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <strong>{requisitos.clausulaTitulo}</strong>
                {requisitos.textoClausula ? (
                  <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                    {String(requisitos.textoClausula).slice(0, 600)}
                    {String(requisitos.textoClausula).length > 600 ? '…' : ''}
                  </Typography>
                ) : null}
              </Alert>
            )}

            {erro ? <Alert severity="error">{erro}</Alert> : null}
            {okMsg ? <Alert severity="success">{okMsg}</Alert> : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Razão social"
                fullWidth
                required
                value={form.razaoSocial}
                onChange={(e) => setForm((f) => ({ ...f, razaoSocial: e.target.value }))}
              />
              <TextField
                label="CNPJ"
                fullWidth
                required
                value={form.cnpj}
                onChange={(e) => setForm((f) => ({ ...f, cnpj: e.target.value }))}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="E-mail"
                fullWidth
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
              <TextField
                label="Valor da proposta (R$)"
                fullWidth
                value={form.valor}
                onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
                helperText="Use vírgula para centavos"
              />
            </Stack>
            <TextField
              label="Observações / resumo da proposta"
              fullWidth
              multiline
              minRows={2}
              value={form.descricao}
              onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
            />

            <Divider />
            <Typography variant="subtitle2" fontWeight={800}>
              Documentos solicitados
            </Typography>
            {docs.map((d) => (
              <Box
                key={d.key}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                  {d.label}
                </Typography>
                <Button
                  component="label"
                  size="small"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {filesByKey[d.key] ? filesByKey[d.key].name : 'Anexar PDF'}
                  <input
                    hidden
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setFilesByKey((prev) => ({ ...prev, [d.key]: file }));
                    }}
                  />
                </Button>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={enviando} sx={{ textTransform: 'none' }}>
          Fechar
        </Button>
        <Button
          variant="contained"
          disabled={enviando || loading || !!okMsg}
          onClick={enviar}
          sx={{ textTransform: 'none', fontWeight: 800 }}
        >
          {enviando ? <CircularProgress size={18} color="inherit" /> : 'Enviar proposta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
